if (!customElements.get('hero-slider')) {
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hero-slider__track');
      this.slides = [...this.querySelectorAll('.hero-slider__slide')];
      this.dots = [...this.querySelectorAll('.hero-slider__dot')];
      this.isSlideMode = this.classList.contains('hero-slider--slide');
      this.autoplay = Number(this.dataset.autoplay) || 0;
      this.index = 0;
      this.pauseReasons = new Set();

      this.setHeight();
      this.onResize = () => {
        this.setHeight();
        this.updateColor();
      };
      window.isMobile?.() || window.addEventListener('resize', this.onResize);

      if (this.slides.length < 2) {
        this.goTo(0, { force: true });
        return;
      }

      this.querySelector('.hero-slider__arrow--prev')?.addEventListener('click', () => this.prev());
      this.querySelector('.hero-slider__arrow--next')?.addEventListener('click', () => this.next());
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.prev();
        if (event.key === 'ArrowRight') this.next();
      });

      // Pause only for keyboard focus: a mouse click on an arrow shouldn't stop autoplay
      this.addEventListener('focusin', (event) => {
        if (event.target.matches(':focus-visible')) this.pause('focus');
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      });

      this.onVisibilityChange = () => (document.hidden ? this.pause('tab') : this.resume('tab'));
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.observer = new IntersectionObserver(([entry]) => {
        entry.isIntersecting ? this.resume('viewport') : this.pause('viewport');
      });
      this.observer.observe(this);

      this.initSwipe();
      this.initThemeEditor();
      this.goTo(0, { force: true });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    setHeight() {
      window.setElementsScreenHeight?.([this]);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    next() {
      this.goTo(this.index + 1);
    }

    goTo(index, { force = false } = {}) {
      const total = this.slides.length;
      const next = (index + total) % total;

      if (next === this.index && !force) return;

      this.index = next;

      if (this.isSlideMode) {
        this.track.style.transform = `translateX(${-100 * next}%)`;
      }

      this.slides.forEach((slide, i) => {
        const active = i === next;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
        this.toggleVideos(slide, active);
      });

      this.dots.forEach((dot, i) => {
        const active = i === next;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
        // Restart the progress animation
        if (active) {
          dot.classList.remove('is-active');
          void dot.offsetWidth;
          dot.classList.add('is-active');
        }
      });

      this.updateColor();
      this.elapsed = 0;
      this.startedAt = Date.now();
      this.schedule();
    }

    updateColor() {
      const { color, colorMobile } = this.slides[this.index].dataset;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.style.setProperty('--hero-slider-color', (isMobile ? colorMobile : color) || '#fff');
    }

    toggleVideos(slide, active) {
      slide.querySelectorAll('video').forEach((video) => {
        // Skip videos hidden by the desktop/mobile media switch
        if (active && video.offsetParent) {
          video.muted = true;
          video.play()?.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.pauseReasons.size) return;
      this.timer = setTimeout(() => this.next(), this.autoplay - (this.elapsed || 0));
      this.startedAt = Date.now() - (this.elapsed || 0);
      this.elapsed = 0;
    }

    pause(reason) {
      if (!this.autoplay) return;
      if (!this.pauseReasons.size && this.startedAt) {
        this.elapsed = Math.min(Date.now() - this.startedAt, this.autoplay);
      }
      this.pauseReasons.add(reason);
      this.classList.add('hero-slider--paused');
      clearTimeout(this.timer);
    }

    resume(reason) {
      if (!this.autoplay || !this.pauseReasons.delete(reason) || this.pauseReasons.size) return;
      this.classList.remove('hero-slider--paused');
      this.schedule();
    }

    initSwipe() {
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let tracking = false;
      let dragged = false;

      this.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.target.closest('.hero-slider__arrow, .hero-slider__dot')) return;
        tracking = true;
        dragged = false;
        startX = event.clientX;
        startY = event.clientY;
        deltaX = 0;
      });

      this.addEventListener('pointermove', (event) => {
        if (!tracking) return;
        deltaX = event.clientX - startX;

        if (!dragged) {
          if (Math.abs(deltaX) < 8) return;
          // Vertical gesture: let the page scroll
          if (Math.abs(event.clientY - startY) > Math.abs(deltaX)) {
            tracking = false;
            return;
          }
          dragged = true;
          this.pause('drag');
          this.classList.add('hero-slider--dragging');
        }

        if (this.isSlideMode) {
          this.track.style.transform = `translateX(calc(${-100 * this.index}% + ${deltaX}px))`;
        }
      });

      const end = () => {
        if (!tracking) return;
        tracking = false;
        if (!dragged) return;

        this.classList.remove('hero-slider--dragging');
        const threshold = Math.min(80, this.offsetWidth * 0.15);

        if (deltaX > threshold) this.prev();
        else if (deltaX < -threshold) this.next();
        else this.goTo(this.index, { force: true });

        this.resume('drag');
      };

      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
      this.addEventListener('pointerleave', end);

      // Don't follow the slide link after a drag
      this.addEventListener('click', (event) => {
        if (dragged) {
          event.preventDefault();
          dragged = false;
        }
      }, true);

      this.addEventListener('dragstart', (event) => event.preventDefault());
    }

    initThemeEditor() {
      if (!window.Shopify?.designMode) return;

      document.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (i === -1) return;
        this.pause('editor');
        this.goTo(i);
      });

      document.addEventListener('shopify:block:deselect', () => this.resume('editor'));
    }
  }

  customElements.define('hero-slider', HeroSlider);
}
